import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { clearPlace, forgetSeen, hasSeen, markSeen, readPlace, rememberPlace } from '../lib/tour';
import type { TourId } from '../lib/tour';

/**
 * The guided tour: which screen has one, which step is showing, and how to move.
 *
 * <p>One tour runs at a time and it belongs to the screen the reader is on. A screen declares its
 * tour with {@link useTour}; the provider decides whether to start it, and {@code ProductTour}
 * draws it. Keeping the decision here rather than in the screens is what stops two tours from
 * opening at once when a route changes.
 *
 * <p>Steps point at elements by a {@code data-tour} attribute rather than a class name. A class is
 * a styling decision and gets renamed; a step that silently stops finding its element is worse
 * than no step, because nothing fails - the tour just quietly gets shorter.
 */

export interface TourStep {
  /**
   * The {@code data-tour} value this step points at. With several, the first one present on the
   * page wins, which is how one definition covers a report that is paid and one that is not.
   */
  anchor: string | string[];
  title: string;
  body: string;
}

export interface TourDefinition {
  id: TourId;
  /**
   * False while the screen is still loading. A tour cannot point at what is not drawn yet, and
   * the report spends its first moment as a spinner.
   */
  ready: boolean;
  steps: TourStep[];
}

/**
 * The element a step points at, or null when this screen does not have it.
 *
 * <p>On the page is not the same as on the screen. The report folds its sections away, so the line
 * naming a source is in the document with no box at all, and lighting it drew a six pixel dot in
 * the corner of the window. An element with no box is treated as absent, which lets a step name a
 * fallback - the folded section itself - and lets one with no fallback be dropped.
 */
export function findAnchor(anchor: string | string[]): HTMLElement | null {
  const names = Array.isArray(anchor) ? anchor : [anchor];
  for (const name of names) {
    for (const found of document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`)) {
      const rect = found.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        return found;
      }
    }
  }
  return null;
}

interface RunningTour {
  id: TourId;
  /** Only the steps whose anchor was on the page when the tour started. */
  steps: TourStep[];
  index: number;
}

interface TourValue {
  /** True when the screen the reader is on has a tour at all. Drives the footer link. */
  available: boolean;
  running: RunningTour | null;
  next: () => void;
  back: () => void;
  /** Skip, Escape, or the end: the tour is done and will not offer itself again. */
  stop: () => void;
  /** Run this screen's tour from the top, whether or not it has been seen. */
  again: () => void;
  register: (definition: TourDefinition | null) => void;
  startIfUnseen: (id: TourId) => void;
}

const TourContext = createContext<TourValue>({
  available: false,
  running: null,
  next: () => undefined,
  back: () => undefined,
  stop: () => undefined,
  again: () => undefined,
  register: () => undefined,
  startIfUnseen: () => undefined,
});

export function TourProvider({ children }: { children: ReactNode }) {
  /*
   * The definition lives in a ref, not in state. Every string in it comes from tx(), so a
   * language change produces a new object on every render; holding it in state would be a render
   * loop. Only whether a tour exists is state, because the footer has to re-render for it.
   */
  const definition = useRef<TourDefinition | null>(null);
  const [available, setAvailable] = useState(false);
  const [running, setRunning] = useState<RunningTour | null>(null);

  const register = useCallback((next: TourDefinition | null) => {
    definition.current = next;
    const offered = next !== null && next.steps.length > 0;
    // The identity-preserving updater means the common case re-renders nothing.
    setAvailable((current) => (current === offered ? current : offered));
  }, []);

  /** Resolves the steps that are actually on the page and opens the tour at one of them. */
  const open = useCallback((id: TourId, at: number) => {
    const current = definition.current;
    if (!current || current.id !== id || !current.ready) {
      return;
    }
    const steps = current.steps.filter((step) => findAnchor(step.anchor) !== null);
    if (steps.length === 0) {
      return;
    }
    const index = at < steps.length ? at : 0;
    rememberPlace(id, index);
    setRunning({ id, steps, index });
  }, []);

  const startIfUnseen = useCallback((id: TourId) => {
    const place = readPlace(id);
    // A remembered place means a tour was interrupted mid-run - by a language change, which
    // remounts everything - so it resumes even though it has been offered before.
    if (place === null && hasSeen(id)) {
      return;
    }
    open(id, place ?? 0);
  }, [open]);

  const again = useCallback(() => {
    const current = definition.current;
    if (!current) {
      return;
    }
    forgetSeen(current.id);
    open(current.id, 0);
  }, [open]);

  const stop = useCallback(() => {
    setRunning((current) => {
      if (current) {
        markSeen(current.id);
      }
      clearPlace();
      return null;
    });
  }, []);

  const next = useCallback(() => {
    setRunning((current) => {
      if (!current) {
        return null;
      }
      if (current.index + 1 >= current.steps.length) {
        markSeen(current.id);
        clearPlace();
        return null;
      }
      rememberPlace(current.id, current.index + 1);
      return { ...current, index: current.index + 1 };
    });
  }, []);

  const back = useCallback(() => {
    setRunning((current) => {
      if (!current || current.index === 0) {
        return current;
      }
      rememberPlace(current.id, current.index - 1);
      return { ...current, index: current.index - 1 };
    });
  }, []);

  const value = useMemo(
    () => ({ available, running, next, back, stop, again, register, startIfUnseen }),
    [available, running, next, back, stop, again, register, startIfUnseen],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTourControls(): TourValue {
  return useContext(TourContext);
}

/**
 * Declares the tour for the screen that calls it, and starts it on a first visit.
 *
 * <p>Called on every render so the steps stay in the reader's current language; the provider
 * absorbs that without re-rendering. The tour itself is started once, when the screen says it is
 * ready, and the screen's tour is withdrawn when it unmounts so a route change cannot leave the
 * footer offering a tour for a page that is gone.
 */
export function useTour(definition: TourDefinition): void {
  const { register, startIfUnseen } = useTourControls();
  const { id, ready } = definition;

  // No dependency array on purpose: the strings change with the language, and this is a ref write.
  useEffect(() => {
    register(definition);
  });

  useEffect(() => () => register(null), [register]);

  useEffect(() => {
    if (ready) {
      startIfUnseen(id);
    }
  }, [id, ready, startIfUnseen]);
}
